/* global moment, TrelloPowerUp */

const Sentry = require('@sentry/browser');
const kApiEndpoint = require('./api-endpoint.js');
const { removeTokenValue } = require('./token-helper');
const {
  TrelloApiError,
  errorForStatus,
  RegistrationError,
  registrationErrorAlert,
} = require('./trello-api-error');

const API_ROOT = 'https://trello.com';

const TOKEN_KEY = 'token';
const tokenOpts = {
  name: 'Butler',
  key: '446cbc1d6532c596ddc610207ead5576',
  scope: 'read,write,account',
};

let activeToken;
let tokenToReset;

const get = function(url, context) {
  return new TrelloPowerUp.Promise(function(resolve, reject) {
    const request = new XMLHttpRequest();
    request.open('GET', url, true);
    request.timeout = 30000;
    request.onload = function() {
      if (request.status === 200) {
        let response;
        try {
          response = JSON.parse(request.responseText);
        } catch (ex) {
          return reject(new TrelloApiError.InvalidResponse(ex.message));
        }
        return resolve(response);
      }
      return reject(errorForStatus(request.status, request.responseText));
    };
    request.ontimeout = function() {
      // Breadcrumbs will detail the specific url that resulted in an error.
      return reject(new TrelloApiError.Timeout(`${context} timeout`));
    };
    request.send();
  });
};

const resetToken = function(t) {
  return t
    .requestToken(tokenOpts)
    .then(token => {
      activeToken = token;
      return t.set('member', 'private', TOKEN_KEY, token).then(() => token);
    })
    .catch(err => {
      // on an old web client Butler doesn't have access to t.requestToken
      Sentry.captureMessage(`Authorization Error: ${err.message}`);
      t.alert({
        display: 'error',
        duration: 15,
        message: 'Butler is unable to complete setup. Please refresh the page.',
      }).catch(() => {});
      throw err;
    });
};

const register = function(t, suppressNotConfirmedWarn) {
  return new TrelloPowerUp.Promise((resolve, reject) => {
    $.ajax(`${kApiEndpoint}auth`, {
      type: 'POST',
      data: JSON.stringify({
        token: activeToken,
        source: 'powerup_trello',
        tz: moment.tz.guess(),
        apikey: tokenOpts.key,
      }),
      contentType: 'application/json',
    })
      .done(response => {
        t.hideAlert().catch(() => {});

        if (response.success) {
          tokenToReset = undefined;
          resolve();
          return;
        }

        // something went wrong
        let err;
        if (response.error === 'MEMBER_NOT_CONFIRMED') {
          err = new RegistrationError.NotConfirmed(response.error);
          if (!suppressNotConfirmedWarn) {
            t.alert({
              display: 'error',
              duration: 10,
              message:
                'Your Trello account is not confirmed. Please confirm your Trello account before using Butler.',
            });
          }
        } else {
          t.alert(registrationErrorAlert);
          err = new RegistrationError.Unknown(response.error);
          Sentry.captureException(err);
        }
        reject(err);
      })
      .fail(jqXHR => {
        Sentry.captureMessage(`Registration request failed: ${jqXHR.status} - ${jqXHR.statusText}`);
        t.alert(registrationErrorAlert);
        reject(new RegistrationError.Unknown(jqXHR.responseText));
        return false;
      });
  });
};

// go get a brand new Trello auth token and then call in and
// register that new token with Butler servers
const reauthorize = function(t) {
  return resetToken(t).then(token =>
    register(t)
      .catch(() => {
        // noop, we already captured what we needed into Sentry
      })
      .then(() => token)
  );
};

const tokenLooksValid = function(token) {
  return (
    typeof token === 'string' &&
    (/^(ATT[A-Z])[A-Za-z0-9]{16,}([A-F0-9]{8})$/i.test(token) || // Signed Tokens
      /^[0-9a-fA-F]{64}$/i.test(token)) // Legacy Tokens
  );
};

const getToken = function(t, issueNew) {
  const trello = t || window._trello;
  return new TrelloPowerUp.Promise((resolve, reject) => {
    if (activeToken && tokenLooksValid(activeToken)) {
      return resolve(activeToken);
    }
    return trello
      .get('member', 'private', TOKEN_KEY)
      .then(storedToken => {
        if (storedToken && tokenLooksValid(storedToken) && tokenToReset !== storedToken) {
          activeToken = storedToken;
          return resolve(storedToken);
        }
        if (issueNew) {
          return resolve(reauthorize(trello));
        }
        return resolve(null);
      })
      .catch(reject);
  });
};

const constructUrl = function(path, params) {
  return getToken().then(token => {
    if (!token) {
      throw new TrelloApiError.Unauthorized(
        `No token for API request. Path=${removeTokenValue(path)}`
      );
    }
    const paramsWithDefaults = {
      key: '446cbc1d6532c596ddc610207ead5576',
      token,
      ...(params || {}),
    };
    const qs = Object.keys(paramsWithDefaults)
      .map(function(k) {
        return `${encodeURIComponent(k)}=${encodeURIComponent(paramsWithDefaults[k])}`;
      })
      .join('&');
    return `${API_ROOT}/1/${path}?${qs}`;
  });
};

const getBoard = function(idBoard, opts) {
  return constructUrl(`boards/${idBoard}`, opts).then(url => get(url, 'getBoard'));
};

const getLists = function(idBoard, opts) {
  return constructUrl(`boards/${idBoard}/lists`, opts).then(url => get(url, 'getLists'));
};

const getCards = function(idBoard, opts) {
  return constructUrl(`boards/${idBoard}/cards`, opts).then(url => get(url, 'getCards'));
};

const getMe = function(opts) {
  return constructUrl('members/me', opts).then(url => get(url, 'getMe'));
};

const getMember = function(idMember) {
  return constructUrl(`members/${idMember}`).then(url => get(url, 'getMember'));
};

const getMyBoards = function(opts) {
  return constructUrl('members/me/boards', opts).then(url => get(url, 'getMyBoards'));
};

const getMyTeams = function() {
  return constructUrl('members/me/organizations').then(url => get(url, 'getMyTeams'));
};

const checkToken = function() {
  return getToken().then(token => {
    if (!token) {
      // no token to be checked is a no-op
      return null;
    }
    return constructUrl(`tokens/${token}`).then(url => get(url, 'checkToken'));
  });
};

// synchronously returns our in memory active Trello token
// You should generally use the normal getToken function over this
// this is mostly a drop in replacement for Trello.token() so we can
// remove our dependence on client.js
const getActiveToken = function() {
  return activeToken;
};

// returns a Promise that will resolve to a Trello token or null
function authorizeSoft(t = window._trello) {
  return getToken(t, false);
}

// returns a Promise that will resolve to a Trello token
// will issue itself a token if it doesn't have one already
function authorize(t = window._trello) {
  return getToken(t, true);
}

// check to see if we have a Trello auth token already
const isAuthorized = function(t) {
  return getToken(t, false)
    .then(token => token && tokenLooksValid(token))
    .catch(() => false);
};

// if we have a token, ensure that it is still good
// if not, issue ourselves a fresh new one
let lastValidityCheck = null;
let pendingValidityCheck;
const ensureTokenValidity = function(t) {
  // don't check again if we checked in the last minute
  if (pendingValidityCheck) {
    return pendingValidityCheck;
  }
  if (lastValidityCheck && lastValidityCheck > Date.now() - 60000) {
    return TrelloPowerUp.Promise.resolve();
  }

  const ErrorEnum = TrelloApiError;

  lastValidityCheck = Date.now();
  pendingValidityCheck = getToken(t)
    .then(token => {
      if (!token) {
        return null;
      }
      return checkToken()
        .then(() => {
          return null;
        })
        .catch(ErrorEnum.Unauthorized, ErrorEnum.Forbidden, ErrorEnum.NotFound, () => {
          // our token is no longer any good

          this.clearMemory();
          tokenToReset = token;
          return reauthorize(t);
        })
        .catch(ErrorEnum, err => {
          // something else is up, but it doesn't necessarily mean
          // we've got a bad token
          Sentry.captureException(err);
        });
    })
    .catch(() => {
      // noop
    })
    .finally(() => {
      pendingValidityCheck = null;
    });
  return pendingValidityCheck;
};

// if we have a token but haven't already marked ourselves as having
// registered, go ahead and ask the Butler servers to register us
let registrationPromise;
// use force=true if the user is interacting in a way that you must
// guarantee that they are registered right now
const registerIfNecessary = function(t, force, CommandStorage, suppressNotConfirmedWarn) {
  // Trello can call capabilities like 'board-buttons' fairly aggressively
  // so we want to be careful to only ever have a single in-flight registration
  // request
  if (registrationPromise) {
    return registrationPromise;
  }

  registrationPromise = TrelloPowerUp.Promise.all([
    t.get('member', 'private', 'registered', false),
    isAuthorized(t),
    CommandStorage.getLocalCommands(),
  ])
    .spread((registered, hasToken, pluginCommands) => {
      if (hasToken && registered) {
        return null;
      }
      if (pluginCommands.commands.length === 0 && !force) {
        // nothing to do
        return null;
      }
      t.alert({
        message: 'Powering-Up, give us a moment...',
        // This will be closed when we successfully powerup, so just give it plenty of time.
        duration: 30,
      });
      if (!hasToken) {
        return resetToken(t).then(() => {
          if (registered && !tokenToReset) {
            return null;
          }
          return register(t, suppressNotConfirmedWarn).then(() =>
            t.set('member', 'private', 'registered', true)
          );
        });
      }
      if (!registered) {
        return register(t, suppressNotConfirmedWarn).then(() =>
          t.set('member', 'private', 'registered', true)
        );
      }
      return null;
    })
    .finally(() => {
      registrationPromise = null;
    });

  return registrationPromise;
};

const clearMemory = function() {
  activeToken = null;
};

const Auth = {
  authorize,
  authorizeSoft,
  checkToken,
  clearMemory,
  ensureTokenValidity,
  getToken,
  getActiveToken,
  reauthorize,
  registerIfNecessary,
  resetToken,
};

const TrelloApi = {
  Auth,
  getBoard,
  getCards,
  getLists,
  getMe,
  getMember,
  getMyBoards,
  getMyTeams,
};

// ensure nothing breaks while we work to get everything modularized
window.Auth = Auth;
window.TrelloApi = TrelloApi;

module.exports = TrelloApi;
